import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination,
  Typography, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, IconButton, Tabs, Tab, Divider,
  Chip, Tooltip,
} from '@mui/material';
import { useMediaQuery } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Layout from '../../components/layout/Layout';
import {
  getMenuMasterItems, addMenuMasterItem, updateMenuMasterItem, deleteMenuMasterItem,
  menuMasterFlagOff, seedMenuMaster,
} from '../../Redux/Actions/menuMasterAction';
import { api } from '../../api/api';
import toast from 'react-hot-toast';
import './menuMaster.css';

const CATEGORIES = [
  { id: 'espresso', label: '에스프레소' },
  { id: 'brewed_coffee', label: '브루커피' },
  { id: 'tea', label: '티' },
  { id: 'chocolate', label: '초코라테' },
  { id: 'cold_drink', label: '콜드 드링크' },
];
const SIZE_KEYS = ['hot_12', 'hot_16', 'ice_16', 'ice_20'];
const SIZE_LABELS = { hot_12: 'HOT 12oz', hot_16: 'HOT 16oz', ice_16: 'ICE 16oz', ice_20: 'ICE 20oz' };
const TIER_OPTIONS = [
  { key: '', label: '기본가' },
  { key: 'tier1', label: 'Tier 1 (소도시)' },
  { key: 'tier2', label: 'Tier 2 (중간)' },
  { key: 'tier3', label: 'Tier 3 (대도시)' },
];
const TIER_COLORS = { tier1: '#4CAF50', tier2: '#FF9800', tier3: '#F44336' };

const emptyItem = {
  category: 'espresso', code: '', name: '', nameEn: '', order: 0, calories: {}, prices: {},
};

function catLabel(id) {
  return CATEGORIES.find(c => c.id === id)?.label || id;
}

function formatCents(v) {
  if (v === undefined || v === null || v === '') return '·';
  return `$${(Number(v) / 100).toFixed(2)}`;
}

function MenuMaster({ user }) {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery('(max-width:700px)');
  const { items, loading, isAdded, isUpdated, isDeleted } = useSelector(s => s.MenuMaster);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [mmPage, setMmPage] = useState(0);
  const [mmRowsPerPage, setMmRowsPerPage] = useState(20);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptyItem);
  const [deleteId, setDeleteId] = useState(null);

  // ★ 티어 가격 관련 state
  const [selectedTier, setSelectedTier] = useState('');
  const [tierPrices, setTierPrices] = useState(null); // { tier1: { americano: {hot_12:375,...} }, ... }
  const [tierLoading, setTierLoading] = useState(false);

  useEffect(() => { dispatch(getMenuMasterItems()); }, []);
  useEffect(() => {
    if (isAdded) { toast.success('추가되었습니다.'); dispatch(menuMasterFlagOff()); setDialogOpen(false); setEditItem(null); }
    if (isUpdated) { toast.success('수정되었습니다.'); dispatch(menuMasterFlagOff()); setDialogOpen(false); setEditItem(null); }
    if (isDeleted) { toast.success('삭제되었습니다.'); dispatch(menuMasterFlagOff()); setDeleteId(null); }
  }, [isAdded, isUpdated, isDeleted]);

  // ★ 티어 가격 로드
  const loadTierPrices = async () => {
    if (tierPrices) return; // 이미 로드됨
    setTierLoading(true);
    try {
      const { data } = await api.get('/menu-master/tier-prices');
      setTierPrices(data.tierPrices);
    } catch (e) {
      console.error('Failed to load tier prices', e);
      toast.error('티어 가격 로드 실패');
    }
    setTierLoading(false);
  };

  const handleTierChange = async (tier) => {
    setSelectedTier(tier);
    if (tier && !tierPrices) await loadTierPrices();
  };

  const filtered = categoryFilter
    ? items.filter(i => i.category === categoryFilter)
    : items;

  const tabValue = categoryFilter || 'all';

  const openAdd = () => {
    setEditItem(null);
    setForm({ ...emptyItem, category: categoryFilter || 'espresso' });
    setDialogOpen(true);
  };
  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      category: item.category,
      code: item.code,
      name: item.name,
      nameEn: item.nameEn || '',
      order: item.order || 0,
      calories: { ...(item.calories || {}) },
      prices: { ...(item.prices || {}) },
    });
    setDialogOpen(true);
  };

  const handleSave = () => {
    if (!form.code?.trim() || !form.name?.trim()) {
      toast.error('코드와 이름을 입력하세요.');
      return;
    }
    const cal = {};
    const prices = {};
    SIZE_KEYS.forEach(k => {
      const v = form.calories[k];
      if (v !== undefined && v !== '') cal[k] = Number(v) || 0;
      const pv = form.prices[k];
      if (pv !== undefined && pv !== '') prices[k] = Number(pv) || 0;
    });
    const payload = { ...form, calories: cal, prices };
    if (editItem) dispatch(updateMenuMasterItem(editItem._id, payload));
    else dispatch(addMenuMasterItem(payload));
  };

  const handleSeed = () => {
    dispatch(seedMenuMaster());
    toast.success('기본 템플릿을 불러왔습니다.');
  };

  // ★ v2 시드 (38개 + 칼로리 + 티어 가격)
  const handleSeedV2 = async () => {
    try {
      await api.post('/menu-master/seed-v2');
      dispatch(getMenuMasterItems());
      toast.success('38개 메뉴 + 칼로리 + 가격 업데이트 완료!');
    } catch (e) {
      toast.error('시드 실패: ' + (e.response?.data?.message || e.message));
    }
  };

  /* 테이블 셀 — 사이즈별 칼로리 or 가격 요약 */
  const sizeSummary = (row, field) => {
    const data = row[field] || {};
    const vals = SIZE_KEYS.map(k => data[k]).filter(v => v !== undefined && v !== null && v !== '');
    if (!vals.length) return '—';
    return SIZE_KEYS.map(k => {
      const v = data[k];
      return v !== undefined && v !== null ? v : '·';
    }).join(' / ');
  };

  /* ★ 티어 가격 표시 (선택된 티어가 있을 때) */
  const tierPriceSummary = (row) => {
    if (!selectedTier || !tierPrices || !tierPrices[selectedTier]) return null;
    const tp = tierPrices[selectedTier][row.code];
    if (!tp) return <span style={{ color: '#999' }}>—</span>;
    return SIZE_KEYS.map(k => {
      const v = tp[k];
      return v !== undefined ? formatCents(v) : '·';
    }).join(' / ');
  };

  return (
    <Layout title="메뉴 마스터 (사이드 메뉴보드)" user={user}>
      <Box sx={{ p: { xs: 1, sm: 2 } }}>
        {/* ★ 버튼 영역: 기존 + 새 시드 + 티어 드롭다운 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          <Button variant="outlined" onClick={handleSeed} size="small" sx={{ borderRadius: 2 }}>
            기본 템플릿 불러오기
          </Button>
          <Tooltip title="38개 메뉴 + 칼로리 + Tier1 기본가를 일괄 등록/업데이트합니다" arrow>
            <Button variant="outlined" onClick={handleSeedV2} size="small"
              sx={{ borderRadius: 2, borderColor: '#551FFF', color: '#551FFF' }}>
              v2 템플릿 (38개+칼로리)
            </Button>
          </Tooltip>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openAdd}
            sx={{ background: '#551FFF', borderRadius: 2 }}>
            메뉴 추가
          </Button>

          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: 12, color: '#888', whiteSpace: 'nowrap' }}>
              티어 가격:
            </Typography>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={selectedTier}
                onChange={(e) => handleTierChange(e.target.value)}
                displayEmpty
                sx={{ borderRadius: 2, fontSize: 13, height: 34 }}
              >
                {TIER_OPTIONS.map(t => (
                  <MenuItem key={t.key} value={t.key}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {t.key && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: TIER_COLORS[t.key] }} />}
                      {t.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedTier && (
              <Chip
                label={`${selectedTier.replace('tier', 'T')} 적용 중`}
                size="small"
                sx={{ bgcolor: TIER_COLORS[selectedTier], color: '#fff', fontWeight: 600, fontSize: 11 }}
              />
            )}
          </Box>
        </Box>

        <Tabs
          value={tabValue}
          onChange={(_, v) => { setCategoryFilter(v === 'all' ? '' : v); setMmPage(0); }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            mb: 2,
            minHeight: 42,
            '& .MuiTab-root': { fontSize: { xs: '0.8rem', sm: '0.875rem' }, minHeight: 42, py: 1 },
          }}
        >
          <Tab label="전체" value="all" />
          {CATEGORIES.map(c => (
            <Tab key={c.id} label={c.label} value={c.id} />
          ))}
        </Tabs>

        {/* 총 건수 + 페이지네이션 */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ fontSize: 13, color: '#888', fontWeight: 600 }}>
            총 {filtered.length}개 메뉴
          </Typography>
          <TablePagination
            component="div"
            count={filtered.length}
            page={mmPage}
            onPageChange={(_, p) => setMmPage(p)}
            rowsPerPage={mmRowsPerPage}
            onRowsPerPageChange={(e) => { setMmRowsPerPage(+e.target.value); setMmPage(0); }}
            rowsPerPageOptions={[20, 50, { label: `전체 (${filtered.length})`, value: filtered.length }]}
            labelRowsPerPage="표시 수:"
            sx={{ '& .MuiTablePagination-toolbar': { minHeight: 36 }, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: 12 } }}
          />
        </Box>

        {!isMobile && (
          <TableContainer component={Paper} className="menu-master-table-wrap" sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <Table size="small" sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow sx={{ background: '#f5f3ff' }}>
                  <TableCell sx={{ fontWeight: 700, width: '10%' }}>카테고리</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '18%' }}>이름</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '22%' }} title="HOT 12oz / HOT 16oz / ICE 16oz / ICE 20oz">
                    칼로리 (H12/H16/I16/I20)
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '22%' }} title="HOT 12oz / HOT 16oz / ICE 16oz / ICE 20oz">
                    {selectedTier ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: TIER_COLORS[selectedTier] }} />
                        {TIER_OPTIONS.find(t => t.key === selectedTier)?.label} 가격
                      </Box>
                    ) : '가격¢ (H12/H16/I16/I20)'}
                  </TableCell>
                  {selectedTier && (
                    <TableCell sx={{ fontWeight: 700, width: '18%', color: '#888' }}>
                      기본가¢ (비교)
                    </TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 700, width: selectedTier ? '10%' : '12%', textAlign: 'center' }}>관리</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={selectedTier ? 6 : 5} align="center">로딩 중...</TableCell></TableRow>
                )}
                {!loading && filtered.length === 0 && (
                  <TableRow><TableCell colSpan={selectedTier ? 6 : 5} align="center" sx={{ py: 4, color: '#888' }}>
                    메뉴가 없습니다. "v2 템플릿" 버튼을 눌러 주세요.
                  </TableCell></TableRow>
                )}
                {!loading && filtered.slice(mmPage * mmRowsPerPage, mmPage * mmRowsPerPage + mmRowsPerPage).map(row => (
                  <TableRow key={row._id} hover>
                    <TableCell sx={{ fontSize: 12 }}>{catLabel(row.category)}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {row.name}
                      {row.nameEn && <span style={{ color: '#888', fontSize: 11 }}> ({row.nameEn})</span>}
                    </TableCell>
                    <TableCell sx={{ fontSize: 11, color: '#555' }}>
                      {sizeSummary(row, 'calories')}
                    </TableCell>
                    <TableCell sx={{ fontSize: 11, color: selectedTier ? TIER_COLORS[selectedTier] : '#555', fontWeight: selectedTier ? 600 : 400 }}>
                      {selectedTier ? tierPriceSummary(row) : sizeSummary(row, 'prices')}
                    </TableCell>
                    {selectedTier && (
                      <TableCell sx={{ fontSize: 11, color: '#999' }}>
                        {sizeSummary(row, 'prices')}
                      </TableCell>
                    )}
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => openEdit(row)}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => setDeleteId(row._id)} sx={{ color: '#e53935' }}><DeleteOutlineIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {isMobile && (
          <Box className="menu-master-mobile-list">
            {loading && <Typography align="center" color="text.secondary" sx={{ py: 3 }}>로딩 중...</Typography>}
            {!loading && filtered.length === 0 && (
              <Paper sx={{ p: 3, textAlign: 'center', color: '#888', borderRadius: 2 }}>메뉴가 없습니다.</Paper>
            )}
            {!loading && filtered.slice(mmPage * mmRowsPerPage, mmPage * mmRowsPerPage + mmRowsPerPage).map(row => (
              <Paper key={row._id} className="menu-master-card" elevation={0}>
                <div className="menu-master-card__head">
                  <div>
                    <Typography className="menu-master-card__title">{row.name}</Typography>
                    <Typography className="menu-master-card__sub">{catLabel(row.category)} · {row.code}</Typography>
                  </div>
                  <Box sx={{ display: 'flex', gap: 0 }}>
                    <IconButton size="small" onClick={() => openEdit(row)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => setDeleteId(row._id)} sx={{ color: '#e53935' }}><DeleteOutlineIcon fontSize="small" /></IconButton>
                  </Box>
                </div>
                {row.nameEn && <Typography sx={{ fontSize: 11, color: '#888', mb: 0.5 }}>{row.nameEn}</Typography>}
                <Typography sx={{ fontSize: 11, color: '#555' }}>
                  칼: {sizeSummary(row, 'calories')}
                </Typography>
                <Typography sx={{ fontSize: 11, color: selectedTier ? TIER_COLORS[selectedTier] : '#555' }}>
                  가: {selectedTier ? tierPriceSummary(row) : sizeSummary(row, 'prices')}
                </Typography>
              </Paper>
            ))}
          </Box>
        )}

        {deleteId && (
          <Dialog open onClose={() => setDeleteId(null)}>
            <DialogTitle>삭제</DialogTitle>
            <DialogContent>이 메뉴 항목을 삭제하시겠습니까?</DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteId(null)}>취소</Button>
              <Button color="error" variant="contained" onClick={() => { dispatch(deleteMenuMasterItem(deleteId)); setDeleteId(null); }}>삭제</Button>
            </DialogActions>
          </Dialog>
        )}

        {/* 추가/수정 다이얼로그 — 사이즈별 칼로리+가격 묶어서 입력 */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editItem ? '메뉴 수정' : '메뉴 추가'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Typography variant="caption" sx={{ color: '#666' }}>
                사이즈별 칼로리와 기본 가격(USD 센트)을 입력합니다. 여기서 등록한 항목은 모든 매장 메뉴보드 템플릿에 공통으로 쓰입니다.
              </Typography>
              <FormControl size="small" fullWidth>
                <InputLabel>카테고리</InputLabel>
                <Select label="카테고리" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                  {CATEGORIES.map(c => <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField size="small" label="코드" value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))} placeholder="americano" disabled={!!editItem} />
              <TextField size="small" label="이름" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="아메리카노" />
              <TextField size="small" label="영문명" value={form.nameEn} onChange={e => setForm(f => ({ ...f, nameEn: e.target.value }))} placeholder="Americano" />

              <Divider sx={{ my: 0.5 }} />
              <Typography variant="subtitle2" sx={{ color: '#551FFF', fontWeight: 700 }}>
                사이즈별 칼로리 + 가격
              </Typography>

              {/* 사이즈별 칼로리/가격 입력 — 한 행에 사이즈명 + 칼로리 + 가격 */}
              {SIZE_KEYS.map(k => (
                <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ minWidth: 80, fontWeight: 600, fontSize: 13, color: k.startsWith('hot') ? '#e65100' : '#0277bd' }}>
                    {SIZE_LABELS[k]}
                  </Typography>
                  <TextField
                    size="small"
                    label="칼로리"
                    type="number"
                    value={form.calories[k] ?? ''}
                    onChange={e => setForm(f => ({ ...f, calories: { ...f.calories, [k]: e.target.value } }))}
                    sx={{ flex: 1 }}
                    inputProps={{ min: 0 }}
                  />
                  <TextField
                    size="small"
                    label="가격(¢)"
                    type="number"
                    value={form.prices[k] ?? ''}
                    onChange={e => setForm(f => ({ ...f, prices: { ...f.prices, [k]: e.target.value } }))}
                    sx={{ flex: 1 }}
                    inputProps={{ min: 0 }}
                  />
                </Box>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>취소</Button>
            <Button variant="contained" onClick={handleSave} sx={{ background: '#551FFF' }}>저장</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
}

export default MenuMaster;
